/**
 * THE MANUAL EXECUTION MACHINE — press a direction, click to open, click to close.
 *
 * <pre>
 *   FLAT ──Open Long / Open Short──▶ AWAITING_OPEN ──click a bar──▶ AWAITING_CLOSE
 *     ▲                                                                   │
 *     └──────── cancel ─────────────────────────── click a bar (booked) ──┘
 * </pre>
 *
 * <h2>⚠ THE SELECTION STEP IS GONE, and that is the point of this shape</h2>
 *
 * <p>It used to be four phases: pick an element, THEN lock a direction, THEN click, THEN click.
 * The first two bought nothing. The element was only ever context for the card — the trade's
 * prices come entirely from the two bars the reader clicks — and the direction lock was a mode
 * whose only job was to make the next click mean something, which pressing "Open Long" says on
 * its own. So the two buttons are permanent and the machine has one fewer state than it had
 * reasons for.
 *
 * <p>The element survives as pure CONTEXT: `withContext` records whichever structure the card is
 * showing without touching the phase, so a trade opened while a PTA channel is selected is still
 * attributed to PTA in the ledger. Nothing has to be selected first.
 *
 * <h2>Who decides what</h2>
 *
 * This module decides NOTHING about whether a trade is a good one. It holds which way the reader
 * has committed and where they clicked — the structural context on the card is guidance, and
 * every price in the ledger came from a click they made. That is the whole difference between
 * this and the CTS card, where a click IS the trade and the engine prices it.
 *
 * <h2>Pure, and it returns REASONS</h2>
 *
 * Every transition is `(state, input) => state`, and an illegal one comes back with a `refusal`
 * rather than silently doing nothing. A gesture that does nothing and says nothing is
 * indistinguishable from a broken control — the same rule the replay's own click refusal follows.
 */

import { BAR_CLOSE } from './analyticaGeometry';
import { OUTCOMES } from './tradeSimEngine';
import { tradeTelemetry } from './tradeSimMetrics';

export const PHASES = Object.freeze({
  FLAT: 'FLAT',
  AWAITING_OPEN: 'AWAITING_OPEN',
  AWAITING_CLOSE: 'AWAITING_CLOSE',
});

/** Nothing pending. Frozen, so the shared empty cannot become a used one. */
export const IDLE_STATE = Object.freeze({
  phase: PHASES.FLAT,
  element: null,
  direction: null,
  open: null,
  refusal: null,
});

/** Is the machine waiting for a click on the chart? The caller gates the bar handler on this. */
export const isAwaitingClick = (state) =>
  !!state && (state.phase === PHASES.AWAITING_OPEN || state.phase === PHASES.AWAITING_CLOSE);

/** True once an OPEN leg is booked and the reader is choosing where to close. */
export const isAwaitingClose = (state) => (state || IDLE_STATE).phase === PHASES.AWAITING_CLOSE;

const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const refuse = (state, reason) => ({ ...state, refusal: reason });

/**
 * Records whichever structure the card is showing. CONTEXT ONLY — the phase does not move.
 *
 * <p>So a channel handle can be clicked at any time to read its geometry, and a trade opened
 * while one is selected carries it into the ledger. Nothing has to be selected to trade, which
 * is what makes the two action buttons permanent.
 *
 * <p>It is refused mid-trade: changing the attribution after an Open has been recorded would
 * book the trade against a structure it was not opened on.
 */
export function withContext(state, element) {
  const prev = state || IDLE_STATE;
  if (!element) return prev;
  if (isAwaitingClick(prev)) {
    return refuse(prev, 'A trade is in progress — finish it, or press Cancel, before '
      + 'switching to another structure.');
  }
  return { ...prev, element, refusal: null };
}

/**
 * Arms the next bar click as an OPEN, in this direction.
 *
 * <p>From FLAT or from AWAITING_OPEN, so a reader who pressed the wrong one can press the other
 * before placing anything — both buttons stay live, which is what "always available" means. It
 * is refused once an Open exists, because a direction changed after the fact would re-sign a
 * P&L the reader already watched being built.
 */
export function beginOpen(state, direction) {
  const prev = state || IDLE_STATE;
  const dir = direction === 'short' ? 'short' : direction === 'long' ? 'long' : null;
  if (!dir) return prev;
  if (prev.phase === PHASES.AWAITING_CLOSE) {
    return refuse(prev, 'A position is already open. Click a bar to close it, or press Cancel.');
  }
  return { ...prev, phase: PHASES.AWAITING_OPEN, direction: dir, open: null, refusal: null };
}

/** Aborts whatever is in flight, keeping the context the card is showing. */
export const cancel = (state) =>
  ({ ...IDLE_STATE, element: (state || IDLE_STATE).element || null });

/** Clears a refusal without changing anything else, so a message can be dismissed. */
export const clearRefusal = (state) =>
  (state && state.refusal ? { ...state, refusal: null } : (state || IDLE_STATE));

/**
 * THE FILL IS THE CLICKED BAR'S CLOSE, never the clicked price — a stated deviation.
 *
 * <p>The brief asks for the click to register a price and a bar. The BAR is honoured and the
 * price is not, for one reason that outranks the others: these trades land in the SAME session
 * ledger as the CTS ones, which fill at the close. A family that could pick any price inside a
 * visible bar's range against one that cannot makes the shared win rate and profit factor
 * meaningless — an operator would buy every low and sell every high and the two halves of the
 * ledger would not be comparable.
 *
 * <p>The clicked level is still recorded as `aimPrice` and shown beside the fill when the two
 * differ, so nothing the reader did is lost; it is simply not what the P&L is computed from.
 */
function fillFor(ohlc, index) {
  return n(ohlc?.[index]?.[BAR_CLOSE]);
}

/**
 * Registers a click on the chart: the Open, then the Close.
 *
 * @param {object} state
 * @param {{index:number, date:string, price:number}} click
 * @param {Array<number[]>} ohlc ECharts order, for the fill
 * @param {number} [maxIndex] THE REPLAY BOUNDARY — the last bar the reader can see
 * @returns {{state: object, trade: object|null}} the trade only on the closing click
 */
export function registerClick(state, click, ohlc = [], maxIndex = null) {
  const prev = state || IDLE_STATE;
  if (!isAwaitingClick(prev) || !click) return { state: prev, trade: null };

  const index = Math.floor(n(click.index) ?? -1);
  /*
   * A LEG MAY NOT LAND PAST THE BOUNDARY.
   *
   * In a replay the bars ahead of the playhead are not drawn, so closing a trade on one would
   * be settling against a price the reader has not been shown — the exact leak the replay
   * exists to prevent, arriving through a gesture that looks like their own decision. Refused
   * with the boundary named, rather than clamped to it: clamping would book a trade at a bar
   * they did not click.
   */
  if (Number.isFinite(maxIndex) && index > Math.floor(maxIndex)) {
    return {
      state: refuse(prev, 'That bar is past the replay boundary — its price is not on screen '
        + 'yet. Step the replay forward, or click a revealed bar.'),
      trade: null,
    };
  }
  const fill = fillFor(ohlc, index);
  if (fill === null) {
    return { state: refuse(prev, 'That bar has no close to fill at.'), trade: null };
  }
  const leg = { index, date: click.date, price: fill, aimPrice: n(click.price) };

  if (prev.phase === PHASES.AWAITING_OPEN) {
    return {
      state: { ...prev, phase: PHASES.AWAITING_CLOSE, open: leg, refusal: null },
      trade: null,
    };
  }

  // ── the closing click ────────────────────────────────────────────────────────────────────
  if (index < prev.open.index) {
    // A close BEFORE the open is time travel, and it would produce a plausible P&L with the
    // sign of the move reversed — the worst kind of wrong, because nothing about it looks odd.
    return {
      state: refuse(prev, 'The close must be at or after the open bar. Click to the right of '
        + `${prev.open.date}, or press Cancel.`),
      trade: null,
    };
  }
  if (index === prev.open.index) {
    // Both legs fill at one bar's close, so the P&L is exactly zero by construction. Refused
    // rather than booked: a guaranteed scratch in the ledger is noise in every rate above it.
    return {
      state: refuse(prev, 'The open and the close are the same bar — that is a scratch, not a '
        + 'trade. Click a later bar.'),
      trade: null,
    };
  }

  return {
    // Back to FLAT with the context kept, so the next trade needs no re-selection and both
    // buttons are live again immediately.
    state: { ...IDLE_STATE, element: prev.element },
    trade: manualTrade(prev.element, prev.direction, prev.open, leg, ohlc),
  };
}

/**
 * The booked trade.
 *
 * <p>Shaped exactly like a `simulateTrade` result, so the session ledger and the card need no
 * branch for it: same `pnlPercent` signed by direction, same three-way outcome, same `priced`
 * flag. What it carries instead of a target and a stop is the two legs the reader clicked.
 *
 * <p>`rMultiple` is NULL, and deliberately so: there is no stop, so there is no R to measure
 * against. Reporting zero would put a real reading in a field that has no value — and the
 * session summary reports mean R only when every realised trade carries one.
 *
 * <p>WITH OR WITHOUT AN ELEMENT. A trade taken with no structure selected is attributed to
 * `MANUAL` and keyed on its own two legs; one taken while a channel is on the card carries that
 * channel's name and date, so the ledger still says which structure it was read against.
 */
export function manualTrade(element, direction, open, close, ohlc = []) {
  const sign = direction === 'short' ? -1 : 1;
  const move = (close.price - open.price) * sign;
  const pnlPercent = (move / open.price) * 100;
  const provider = element?.provider || 'MANUAL';
  // The structure's own start date when there is one, otherwise the bar the trade opened on —
  // so the id is unique either way without a branch at the call site.
  const scope = element?.date1 || open.date;
  return {
    provider,
    // EVERY EXECUTION IS ITS OWN ENTRY. The id carries the direction and both legs, because a
    // long and a short opened on the same bar are two trades the reader took — and so is the
    // same round trip taken twice. `date` is the readable half; the ledger keys on this.
    executionId: `${provider}:${scope}:${direction}:${open.index}-${close.index}`,
    date: `${open.date}→${close.date}`,
    signal: element || null,
    manual: true,
    direction: direction === 'short' ? 'down' : 'up',
    priced: true,
    index: open.index,
    openLeg: open,
    closeLeg: close,
    entryPrice: open.price,
    exitPrice: close.price,
    exitIndex: close.index,
    exitReason: 'closed by the operator',
    barsHeld: close.index - open.index,
    horizon: close.index - open.index,
    target: null,
    stop: null,
    risk: null,
    targetAtr: null,
    stopAtr: null,
    pnl: move,
    pnlPercent,
    // NO R, and that is not an omission: a manual trade carries no stop, so there is no risk
    // distance to divide by. Deriving one from an ATR the reader never chose would put a
    // figure on the card that no decision of theirs produced — and would then be averaged
    // into a session mean R beside trades that really have one.
    rMultiple: null,
    /*
     * THE SAME TELEMETRY THE ENGINE'S OWN TRADES CARRY, so one ledger reports one set of
     * columns. The excursions and the benchmark need only the two legs and the bars; `maeR`
     * and `mfeR` come back null for want of a risk distance, which the panel renders as n/a.
     */
    ...tradeTelemetry({
      ohlc,
      entryIndex: open.index,
      exitIndex: close.index,
      entryPrice: open.price,
      sign,
      risk: null,
      pnlPercent,
    }),
    outcome: move > 0 ? OUTCOMES.WIN : move < 0 ? OUTCOMES.LOSS : OUTCOMES.NEUTRAL,
  };
}

/**
 * What the dialog tells the reader to do next. One sentence per phase, never a blank panel.
 *
 * <p>`replay` adds the second half of the gesture, and only in a replay: there the grid is split
 * so that a click on a candle trades and a click in the air around it steps the playhead
 * forward. Saying so is the whole of the distinction — the two zones look identical, so a
 * reader who is not told will click empty space, watch the chart move, and conclude the trade
 * was lost. Outside a replay there is nothing to step, so the sentence would describe a gesture
 * that does not exist.
 */
export function promptFor(state, { replay = false } = {}) {
  const s = state || IDLE_STATE;
  const stepping = replay
    ? ' Click empty space, above or below the bars, to step the replay forward.'
    : '';
  switch (s.phase) {
    case PHASES.AWAITING_OPEN:
      return `Click directly on a bar to OPEN the ${s.direction}.${stepping}`;
    case PHASES.AWAITING_CLOSE:
      return `Click directly on a later bar to CLOSE the ${s.direction}. Opened at `
        + `${s.open.price.toFixed(2)} on ${s.open.date}.${stepping}`;
    default:
      return `Press Open Long or Open Short, then click a bar to enter.${stepping}`;
  }
}

export default registerClick;
